/**
 * Аптеки сети: `GET/POST /admin/stores`, `GET/PUT/DELETE /admin/stores/{id}`.
 *
 * `open_now` считает сервер в ташкентском времени — сами по часам не гадаем,
 * иначе аптека на экране открывалась бы по часам браузера (docs/frontend/README.md).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { parseMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type StoreDto = components['schemas']['Store']
type DayHoursDto = components['schemas']['DayHours']

export interface DayHours {
  open: string
  close: string
}

export interface Store {
  id: number
  name: string
  district: string
  address: string
  metro: string
  phone: string
  manager: string
  lat: number | null
  lng: number | null
  /** Круглосуточная: расписание по дням тогда не используется. */
  is247: boolean
  /** Понедельник…воскресенье; null — выходной. */
  hours: (DayHours | null)[]
  acceptsPickup: boolean
  acceptsDelivery: boolean
  /** Аптека имеет право отпускать рецептурные препараты. */
  dispensesRx: boolean
  active: boolean
  /** Открыта прямо сейчас — по ташкентскому времени сервера. */
  openNow: boolean
  /** Заказов через аптеку; сервер считает его только для списка в админке. */
  ordersCount: number
  /** Когда FOM последний раз прислал остатки аптеки, мс; 0 — ещё ни разу. Только в админке. */
  fomSyncedAt: number
  /** Когда аптеку завели в системе, мс; 0 — сервер не прислал. */
  createdAt: number
}

/** Пустая неделя: у формы всегда семь дней, даже если сервер прислал меньше. */
const CLOSED_WEEK: (DayHours | null)[] = [null, null, null, null, null, null, null]

function toHours(dto: DayHoursDto[] | undefined): (DayHours | null)[] {
  return CLOSED_WEEK.map((_, i) => {
    const day = dto?.[i]
    return day?.open && day.close ? { open: day.open, close: day.close } : null
  })
}

export function toStore(dto: StoreDto): Store {
  return {
    id: dto.id ?? 0,
    name: dto.name,
    district: dto.district,
    address: dto.address,
    metro: dto.metro ?? '',
    phone: dto.phone ?? '',
    manager: dto.manager ?? '',
    lat: dto.lat ?? null,
    lng: dto.lng ?? null,
    is247: dto.is_24_7 ?? false,
    hours: toHours(dto.hours),
    acceptsPickup: dto.accepts_pickup ?? true,
    acceptsDelivery: dto.accepts_delivery ?? false,
    dispensesRx: dto.dispenses_rx ?? false,
    active: dto.is_active ?? true,
    openNow: dto.open_now ?? false,
    ordersCount: dto.orders_count ?? 0,
    fomSyncedAt: parseMs(dto.fom_synced_at),
    createdAt: parseMs(dto.created_at),
  }
}

/** Тело формы аптеки: сервер принимает её целиком, а не по полям. */
export function toStoreInput(store: Store): components['schemas']['StoreInput'] {
  return {
    name: store.name,
    district: store.district,
    address: store.address,
    metro: store.metro,
    phone: store.phone,
    manager: store.manager,
    lat: store.lat,
    lng: store.lng,
    is_24_7: store.is247,
    hours: store.hours.map((day) => (day ? { open: day.open, close: day.close } : null)),
    accepts_pickup: store.acceptsPickup,
    accepts_delivery: store.acceptsDelivery,
    dispenses_rx: store.dispensesRx,
    is_active: store.active,
  }
}

export async function fetchStores(api: ApiClient): Promise<Store[]> {
  const page = await api.get<{ items?: StoreDto[] }>('/admin/stores')
  return (page.items ?? []).map(toStore)
}

export async function saveStore(api: ApiClient, store: Store): Promise<Store> {
  const dto = store.id
    ? await api.put<StoreDto>(`/admin/stores/${store.id}`, toStoreInput(store))
    : await api.post<StoreDto>('/admin/stores', toStoreInput(store))
  return toStore(dto)
}

export function useStores(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: keys.stores,
    queryFn: () => fetchStores(api),
    enabled: options.enabled ?? true,
  })
}

/** Одна аптека берётся из списка: он короткий и уже загружен на соседнем экране. */
export function useStore(id: number) {
  const list = useStores()
  return { ...list, data: list.data?.find((s) => s.id === id) }
}

export function useSaveStore() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (store: Store) => saveStore(api, store),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.stores }),
  })
}

export function useDeleteStore() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del<unknown>(`/admin/stores/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.stores }),
  })
}

/** Строка остатков аптеки: что лежит на полке, по данным FOM. */
export interface ShelfLine {
  productId: number
  name: string
  fomCode: string
  qty: number
  price: number
  /** Когда FOM прислал эту строку, мс. */
  syncedAt: number
}

export interface ShelfPage {
  items: ShelfLine[]
  total: number
}

export interface ShelfQuery {
  q: string
  page: number
  pageSize: number
}

/**
 * Остатки одной аптеки — страницами, с поиском по названию и коду FOM.
 * Только чтение: остатки ведёт FOM (`GET /admin/stores/{id}/stock`).
 */
export function useStoreStock(id: number, query: ShelfQuery, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.store(id), 'stock', query],
    queryFn: async (): Promise<ShelfPage> => {
      const dto = await api.get<{
        items?: {
          product_id?: number
          name?: string
          fom_code?: string
          qty?: number
          price?: number
          synced_at?: string
        }[]
        total?: number
      }>(`/admin/stores/${id}/stock`, {
        query: { q: query.q.trim() || undefined, page: query.page, page_size: query.pageSize },
      })
      return {
        items: (dto.items ?? []).map((line) => ({
          productId: line.product_id ?? 0,
          name: line.name ?? '',
          fomCode: line.fom_code ?? '',
          qty: line.qty ?? 0,
          price: line.price ?? 0,
          syncedAt: parseMs(line.synced_at),
        })),
        total: dto.total ?? 0,
      }
    },
    placeholderData: keepPreviousData,
    enabled,
  })
}
